import type { FormEvent } from "react";

import { Button, Input } from "@/atoms";
import { strings } from "@/constants";
import { ButtonTypeEnum, ButtonVariantEnum, InputTypeEnum } from "@/enums";

export const ProfileForm = ({
    companyName,
    confirmPassword,
    email,
    isAdmin,
    isSaving,
    name,
    onCancel,
    onChangeCompanyName,
    onChangeConfirmPassword,
    onChangeEmail,
    onChangeName,
    onChangePassword,
    onChangePhone,
    onSubmit,
    password,
    passwordError,
    phone,
}: {
    companyName: string,
    confirmPassword: string,
    email: string,
    isAdmin: boolean,
    isSaving: boolean,
    name: string,
    onCancel: () => void,
    onChangeCompanyName: (value: string) => void,
    onChangeConfirmPassword: (value: string) => void,
    onChangeEmail: (value: string) => void,
    onChangeName: (value: string) => void,
    onChangePassword: (value: string) => void,
    onChangePhone: (value: string) => void,
    onSubmit: (event: FormEvent<HTMLFormElement>) => void,
    password: string,
    passwordError: string,
    phone: string,
}) => (
    <form
        className="flex h-full flex-col"
        onSubmit={onSubmit}
    >
        <div
            className="
                flex
                items-center
                gap-3
                border-b
                border-gray-100
                bg-gradient-to-r
                from-primary/5
                to-transparent
                px-4
                py-4
                sm:px-6
                sm:py-5
            "
        >
            <div
                className="
                    flex
                    h-9
                    w-9
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-primary/10
                    text-primary
                "
            >
                <svg
                    fill="none"
                    height="16"
                    viewBox="0 0 24 24"
                    width="16"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12 20h9M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.8"
                    />
                </svg>
            </div>
            <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-text-primary">{strings.profile.editButton}</h2>
                <p className="truncate text-xs text-text-secondary">{strings.profile.title}</p>
            </div>
        </div>
        <div
            className="
                grid
                flex-1
                gap-4
                px-4
                py-5
                sm:gap-5
                sm:px-6
                sm:py-6
                md:grid-cols-2
            "
        >
            <Input
                disabled={isSaving}
                label={strings.profile.nameLabel}
                type={InputTypeEnum.TEXT}
                value={name}
                onChange={({ target }) => onChangeName(target.value)}
            />
            <Input
                disabled={isSaving}
                label={strings.profile.emailLabel}
                type={InputTypeEnum.EMAIL}
                value={email}
                onChange={({ target }) => onChangeEmail(target.value)}
            />
            {!isAdmin && (
                <>
                    <Input
                        disabled={isSaving}
                        label={strings.profile.companyLabel}
                        type={InputTypeEnum.TEXT}
                        value={companyName}
                        onChange={({ target }) => onChangeCompanyName(target.value)}
                    />
                    <Input
                        disabled={isSaving}
                        label={strings.profile.phoneLabel}
                        type={InputTypeEnum.TEL}
                        value={phone}
                        onChange={({ target }) => onChangePhone(target.value)}
                    />
                </>
            )}
        </div>
        <div
            className="
                border-t
                border-gray-100
                px-4
                py-5
                sm:px-6
                sm:py-6
            "
        >
            <div className="mb-4 flex items-center gap-3">
                <div
                    className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-primary/10
                        text-primary
                    "
                >
                    <svg
                        fill="none"
                        height="14"
                        viewBox="0 0 24 24"
                        width="14"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 1 0-8 0v4h8z"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.8"
                        />
                    </svg>
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-text-primary">{strings.profile.passwordSectionTitle}</h3>
                    <p className="truncate text-xs text-text-secondary">{strings.profile.passwordSectionDescription}</p>
                </div>
            </div>
            <div
                className="
                    grid
                    gap-4
                    sm:gap-5
                    md:grid-cols-2
                "
            >
                <Input
                    disabled={isSaving}
                    isShowPasswordToggle
                    label={strings.profile.newPasswordLabel}
                    placeholder={strings.profile.newPasswordPlaceholder}
                    type={InputTypeEnum.PASSWORD}
                    value={password}
                    onChange={({ target }) => onChangePassword(target.value)}
                />
                <Input
                    disabled={isSaving || !password}
                    errorMessage={passwordError}
                    hasError={Boolean(passwordError)}
                    isShowPasswordToggle
                    label={strings.profile.confirmPasswordLabel}
                    placeholder={strings.profile.confirmPasswordPlaceholder}
                    type={InputTypeEnum.PASSWORD}
                    value={confirmPassword}
                    onChange={({ target }) => onChangeConfirmPassword(target.value)}
                />
            </div>
        </div>
        <div
            className="
                flex
                flex-col-reverse
                gap-3
                border-t
                border-gray-100
                bg-gray-50/50
                px-4
                py-4
                sm:flex-row
                sm:items-center
                sm:justify-end
                sm:px-6
                sm:py-4
            "
        >
            <Button
                disabled={isSaving}
                type={ButtonTypeEnum.BUTTON}
                variant={ButtonVariantEnum.GHOST}
                onClick={onCancel}
            >
                {strings.profile.cancelEdit}
            </Button>
            <Button
                isLoading={isSaving}
                type={ButtonTypeEnum.SUBMIT}
                variant={ButtonVariantEnum.PRIMARY}
            >
                {strings.profile.saveButton}
            </Button>
        </div>
    </form>
);
